import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Clock, Check } from 'lucide-react';
import { useAppointmentsInRange } from '@/hooks/useAppointments';
import { useClinicMembers, type ClinicMemberOption } from '@/hooks/useClinicMembers';
import { useFilterSlotsByResourceCapacity } from '@/hooks/useSchedulingResources';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicHoursExceptions } from '@/hooks/useClinicHoursExceptions';
import { Button } from '@/components/ui/Button';
import { WORKING_HOURS_DAY_KEYS } from '@/utils/appointmentCalendarDates';
import {
  civilDateFromLocalDate,
  exceptionByDate,
  getEffectiveBookableWindows,
  parseWeeklyHours,
  resolveClinicHoursForLocalDate,
  slotStartMinutesWithinWindows,
} from '@/lib/clinicHours';
import { unionPeriods } from '@/lib/practitionerAvailability';
import {
  toMemberDayExceptions,
  useMemberScheduleExceptions,
} from '@/hooks/usePractitionerDayHours';

interface AvailabilityPickerProps {
  clinicId: string;
  serviceId?: string | null;
  members?: ClinicMemberOption[];
  selectedClinicianId: string | null;
  selectedDateTime: Date | null;
  duration: number; // in minutes
  excludeAppointmentId?: string | null; // When editing, exclude current appointment
  onSelect: (dateTime: Date, clinicianId: string | null) => void;
  onClinicianChange?: (clinicianId: string | null) => void;
}

const SLOT_INTERVAL = 15; // minutes

function startOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(23, 59, 59, 999);
  return date;
}

function addDays(d: Date, n: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function AvailabilityPicker({
  clinicId,
  serviceId,
  members: membersOverride,
  selectedClinicianId,
  selectedDateTime,
  duration,
  excludeAppointmentId,
  onSelect,
  onClinicianChange,
}: AvailabilityPickerProps) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(
    selectedDateTime ? startOfDay(selectedDateTime) : startOfDay(new Date()),
  );
  const [viewStartDate, setViewStartDate] = useState<Date>(startOfDay(new Date()));

  const { data: loadedMembers } = useClinicMembers(clinicId);
  const members = membersOverride ?? loadedMembers;
  const { data: clinic } = useClinic(clinicId);

  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(viewStartDate, i));
  }, [viewStartDate]);

  const weekFrom = civilDateFromLocalDate(weekDates[0]);
  const weekTo = civilDateFromLocalDate(weekDates[6]);
  const { data: hourExceptions } = useClinicHoursExceptions(clinicId, weekFrom, weekTo);
  const { data: memberExceptions } = useMemberScheduleExceptions(clinicId, weekFrom, weekTo);
  const exceptionsMap = useMemo(() => exceptionByDate(hourExceptions), [hourExceptions]);
  const weeklyHours = useMemo(
    () => parseWeeklyHours(clinic?.business_hours),
    [clinic?.business_hours],
  );

  const hourWindows = useMemo(() => {
    const date = new Date(selectedDate);
    const iso = civilDateFromLocalDate(date);
    const resolved = resolveClinicHoursForLocalDate({
      date,
      weeklyHours,
      exception: exceptionsMap.get(iso) ?? null,
    });
    if (resolved.source === 'exception' && resolved.exceptionType === 'closed') return [];
    const dayKey = WORKING_HOURS_DAY_KEYS[date.getDay()];
    const targetMembers = selectedClinicianId
      ? (members ?? []).filter((member) => member.user_id === selectedClinicianId)
      : (members ?? []);
    return unionPeriods(
      targetMembers.map((member) =>
        getEffectiveBookableWindows({
          clinicPeriods: resolved.periods,
          clinicSource: resolved.source,
          clinicExceptionType: resolved.exceptionType,
          clinicianDay: member.working_hours[dayKey],
          memberExceptions: toMemberDayExceptions(memberExceptions, member.membership_id, iso),
        }),
      ),
    );
  }, [selectedDate, weeklyHours, exceptionsMap, selectedClinicianId, members, memberExceptions]);

  const slotRange = useMemo(() => {
    const starts: string[] = [];
    const ends: string[] = [];
    const date = new Date(selectedDate);
    for (const slotStartMin of slotStartMinutesWithinWindows(
      hourWindows,
      duration,
      SLOT_INTERVAL,
    )) {
      const slotTime = new Date(date);
      slotTime.setHours(Math.floor(slotStartMin / 60), slotStartMin % 60, 0, 0);
      starts.push(slotTime.toISOString());
      ends.push(new Date(slotTime.getTime() + duration * 60_000).toISOString());
    }
    return { starts, ends };
  }, [hourWindows, selectedDate, duration]);

  const { data: resourceFlags } = useFilterSlotsByResourceCapacity(
    clinicId,
    serviceId,
    slotRange.starts,
    slotRange.ends,
    excludeAppointmentId,
  );

  const { data: appointments, isLoading } = useAppointmentsInRange(clinicId, {
    start: startOfDay(weekDates[0]).toISOString(),
    end: endOfDay(weekDates[6]).toISOString(),
    clinicianId: selectedClinicianId,
  });

  const timeSlots = useMemo(() => {
    return slotRange.starts.map((iso, index) => {
      const slotTime = new Date(iso);
      const slotEnd = new Date(slotRange.ends[index]);
      const hasConflict = appointments?.some((apt) => {
        if (apt.id === excludeAppointmentId) return false;
        const aptStart = new Date(apt.starts_at);
        const aptEnd = new Date(apt.ends_at);
        return slotTime < aptEnd && slotEnd > aptStart;
      });
      return {
        time: slotTime,
        available: !hasConflict && resourceFlags?.[index] !== false,
      };
    });
  }, [slotRange, appointments, excludeAppointmentId, resourceFlags]);

  const handlePreviousWeek = () => {
    setViewStartDate(addDays(viewStartDate, -7));
  };

  const handleNextWeek = () => {
    setViewStartDate(addDays(viewStartDate, 7));
  };

  const handleToday = () => {
    const today = startOfDay(new Date());
    setViewStartDate(today);
    setSelectedDate(today);
  };

  const isToday = (date: Date) => {
    const today = startOfDay(new Date());
    return date.getTime() === today.getTime();
  };

  const isSelected = (date: Date) => {
    return date.getTime() === selectedDate.getTime();
  };

  const isSelectedSlot = (slotTime: Date) => {
    if (!selectedDateTime) return false;
    return slotTime.getTime() === new Date(selectedDateTime).getTime();
  };

  return (
    <div className="space-y-4" data-testid="availability-picker">
      {/* Employee Selector */}
      <div>
        <label className="mb-2 block text-sm font-medium text-ds-text">
          {t('appointments.availability.select_clinician', 'Vælg behandler')}
        </label>
        <select
          value={selectedClinicianId ?? ''}
          onChange={(e) => onClinicianChange?.(e.target.value || null)}
          className="ds-field"
        >
          <option value="">
            {t('appointments.availability.any_clinician', 'Alle behandlere')}
          </option>
          {members?.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button type="button" variant="tertiary" size="icon" onClick={handlePreviousWeek}>
          <ChevronLeft />
        </Button>

        <Button type="button" variant="link" onClick={handleToday}>
          {t('appointments.availability.today', 'I dag')}
        </Button>

        <Button type="button" variant="tertiary" size="icon" onClick={handleNextWeek}>
          <ChevronRight />
        </Button>
      </div>

      {/* Week View - Date Selector */}
      <div className="grid grid-cols-7 gap-2">
        {weekDates.map((date) => {
          const isPast = date < startOfDay(new Date());
          const selected = isSelected(date);
          const today = isToday(date);

          return (
            <button
              key={date.toISOString()}
              type="button"
              data-testid="availability-day"
              onClick={() => setSelectedDate(date)}
              disabled={isPast}
              className={`relative rounded-lg p-3 text-center transition-all ${
                selected
                  ? 'bg-ds-teal text-ds-on-accent ring-2 ring-ds-teal ring-offset-2 ring-offset-ds-card'
                  : today
                    ? 'bg-ds-teal-tint text-ds-teal'
                    : 'bg-ds-card text-ds-text hover:bg-ds-row-hover'
              } ${isPast ? 'cursor-not-allowed opacity-40' : ''} border border-ds-line`}
            >
              <div className="text-xs font-medium">{formatDate(date).split(' ')[0]}</div>
              <div className="text-lg font-bold">{date.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Time Slots */}
      <div className="border-t border-ds-line pt-4">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-ds-muted" />
          <h4 className="text-sm font-medium text-ds-text">
            {t('appointments.availability.available_times', 'Ledige tider')} -{' '}
            {formatDate(selectedDate)}
          </h4>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-ds-muted">
            {t('appointments.availability.loading', 'Indlæser...')}
          </div>
        ) : (
          <div className="grid max-h-[400px] grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
            {timeSlots.map(({ time, available }) => {
              const selected = isSelectedSlot(time);
              const isPast = time < new Date();

              return (
                <button
                  key={time.toISOString()}
                  type="button"
                  data-testid="availability-time"
                  data-available={available && !isPast ? 'true' : 'false'}
                  onClick={() => onSelect(time, selectedClinicianId)}
                  disabled={!available || isPast}
                  className={`relative rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                    selected
                      ? 'bg-ds-teal text-ds-on-accent ring-2 ring-ds-teal'
                      : available && !isPast
                        ? 'bg-ds-green-tint text-ds-green-ink hover:bg-ds-green-tint'
                        : 'cursor-not-allowed bg-ds-disabled text-ds-muted'
                  } border ${
                    selected
                      ? 'border-ds-teal'
                      : available && !isPast
                        ? 'border-ds-green-line'
                        : 'border-ds-line'
                  } `}
                >
                  {formatTime(time)}
                  {selected && <Check className="absolute right-1 top-1 h-3 w-3" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 border-t border-ds-line pt-2 text-xs text-ds-muted">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded border border-ds-green-line bg-ds-green-tint" />
          <span>{t('appointments.availability.available', 'Ledig')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded border border-ds-line bg-ds-disabled" />
          <span>{t('appointments.availability.busy', 'Optaget')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded border border-ds-teal bg-ds-teal" />
          <span>{t('appointments.availability.selected', 'Valgt')}</span>
        </div>
      </div>
    </div>
  );
}
